import { router } from "@/router";
import { useAuthStore } from "@/stores/auth";
import { useBrandSwitcherStore } from "@/stores/brandSwitcher";

/**
 * `fetch` wrapper for endpoints under /api/v1/* that need a JWT
 * bearer header. Centralises two things the typed `openapi-fetch`
 * client gives us automatically but plain `fetch()` callers don't:
 *
 *  - Reads the access token from the Pinia auth store and injects
 *    it as the Authorization header.
 *  - On a 401 response (expired or revoked session), clears the
 *    auth state and bounces to /login carrying the current path as
 *    `redirect`, so after re-login the user lands back where they
 *    were — instead of staring at a blank page full of "Failed to
 *    load X" errors.
 *
 * Stores that use action POSTs / PATCHes (DTOs without @ApiBody)
 * should call this instead of `fetch` so 401 handling stays
 * consistent across the GUI.
 */
export async function authFetch(
  input: string,
  init: RequestInit = {},
): Promise<Response> {
  const auth = useAuthStore();
  const switcher = useBrandSwitcherStore();

  const buildHeaders = (): Headers => {
    const h = new Headers(init.headers ?? {});
    if (auth.tokens?.accessToken && !h.has("Authorization")) {
      h.set("Authorization", `Bearer ${auth.tokens.accessToken}`);
    }
    // Brand-switcher: when an internal user has picked a brand, every
    // admin API request carries `X-Brand-Code` so the BrandContext
    // middleware on /api/v1/* can scope queries to that brand. No
    // header = admin / cross-brand (the default).
    if (switcher.selectedCode && !h.has("X-Brand-Code")) {
      h.set("X-Brand-Code", switcher.selectedCode);
    }
    return h;
  };

  let res = await fetch(input, { ...init, headers: buildHeaders() });

  // Yhden retryn refresh-flow: jos access-token vanheni juuri ennen
  // pyyntöä (esim. käyttäjä palasi pitkän tauon jälkeen, mutta on yhä
  // refresh-tokenin elinkaaren sisällä), yritetään silent-refresh
  // KERRAN. Onnistuessa toistetaan alkuperäinen pyyntö uudella
  // tokenilla. Epäonnistuminen putoaa läpi 401-polkuun.
  //
  // Tärkeää: ei retryata jos pyyntö ON ITSE auth-refresh-endpoint
  // (muuten saataisiin ikuinen loop). Yhdistetään tämä polkuvertailulla.
  // F-10: refresh-token elää HttpOnly-cookiessa, joten emme voi enää
  // tarkistaa "onko refresh-token saatavilla" storesta. Yritetään
  // refresh joka tapauksessa kun ollaan kirjautuneena (= access-token
  // storessa) ja saatiin 401. Cookie joko on tai ei ole; jos sitä ei
  // ole, backend palauttaa 401 josta `auth.refresh()` tekee logout:n.
  if (
    res.status === 401 &&
    !input.includes("/api/v1/auth/refresh") &&
    auth.tokens?.accessToken
  ) {
    const ok = await auth.refresh();
    if (ok) {
      // body voi olla luettu kerran (Response.body on stream); init
      // sisältää sen sellaisenaan, joten uudelleenkäytetään samaa
      // init-objektia + tuoreet headerit.
      res = await fetch(input, { ...init, headers: buildHeaders() });
    }
  }

  if (res.status === 401) {
    handleUnauthenticated();
  }
  return res;
}

/**
 * Shared 401 handler — logout + redirect to /login. Exposed so the
 * typed `openapi-fetch` middleware can call the same thing.
 */
export function handleUnauthenticated(): void {
  const auth = useAuthStore();
  const wasAuthenticated = auth.isAuthenticated;
  if (wasAuthenticated) {
    auth.logout();
    auth.error = "Kirjautumissessiosi vanheni. Kirjaudu uudelleen.";
  }
  const current = router.currentRoute.value;
  if (current.name === "login") return;
  void router.push({
    path: "/login",
    query: { redirect: current.fullPath },
  });
}
